import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ExamDictionaryEntry,
  ExamRecord,
  ExamReferenceProfile,
  ExamValue,
} from '@/entities/exam/types'
import { createId } from '@/shared/lib/id'
import { includesNormalized, normalizeIdentity } from '@/shared/lib/normalize'
import { getErrorMessage, isLikelyOfflineError, retryOnNetworkOrAuthError } from '@/shared/lib/network'
import { indexedDBJSON } from '@/shared/lib/persistence'
import { examsRepository, type ApiExamResult } from '@/shared/repositories/exams/examsRepository'
import { AppError } from '@/shared/lib/appError'
import { logClientError } from '@/shared/lib/safeClientLog'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import { useSessionStore } from '@/features/session/model/sessionStore'

interface AddExamPayload {
  patientId: string
  examName: string
  date: string
  value: ExamValue
  notes?: string
  laboratory?: string
  referenceProfile?: ExamReferenceProfile
  warningThresholdPercent?: number
}

interface ExamStoreState {
  exams: ExamRecord[]
  examDictionary: ExamDictionaryEntry[]
  loaded: boolean
  fetchExams: () => Promise<void>
  addExam: (payload: AddExamPayload) => ExamRecord | null
  importExams: (payloads: AddExamPayload[]) => { created: number; skipped: number }
  getExamsByPatient: (patientId: string) => ExamRecord[]
  getRecentExams: (limit?: number) => ExamRecord[]
  getExamSuggestions: (query: string, limit?: number) => string[]
}

const sortExamsByDateDesc = (exams: ExamRecord[]): ExamRecord[] =>
  [...exams].sort(
    (left, right) => new Date(right.date).getTime() - new Date(left.date).getTime(),
  )

const buildExamDictionary = (exams: ExamRecord[]): ExamDictionaryEntry[] => {
  const map = new Map<string, ExamDictionaryEntry>()
  const ordered = [...exams].sort(
    (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime(),
  )
  ordered.forEach((exam) => {
    map.set(exam.normalizedExamName, {
      normalizedExamName: exam.normalizedExamName,
      examName: exam.examName,
      updatedAt: exam.date,
    })
  })
  return [...map.values()].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  )
}

const apiToExamRecord = (r: ApiExamResult): ExamRecord => ({
  id: r.id,
  patientId: r.patientId,
  examName: r.examName,
  normalizedExamName: r.normalizedExamName,
  date: r.date,
  value: r.numericValue ?? r.textValue ?? r.value,
  notes: r.notes,
  laboratory: r.laboratory,
  warningThresholdPercent: r.warningThresholdPercent,
  referenceProfile:
    r.refMin != null || r.refMax != null
      ? {
          base: {
            min: r.refMin,
            max: r.refMax,
            laboratory: r.laboratory,
            warningThresholdPercent: r.warningThresholdPercent,
          },
        }
      : undefined,
})

const seedExams: ExamRecord[] = []
const seedExamDictionary = buildExamDictionary(seedExams)

const normalizeDate = (value: string): string => {
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return new Date().toISOString()
  return parsedDate.toISOString()
}

const createExamRecord = (payload: AddExamPayload): ExamRecord => {
  const cleanNotes = payload.notes?.trim() ?? ''
  const cleanLab = payload.laboratory?.trim() ?? ''
  return {
    id: createId(),
    patientId: payload.patientId,
    examName: payload.examName.trim(),
    normalizedExamName: normalizeIdentity(payload.examName),
    date: normalizeDate(payload.date),
    value: payload.value,
    notes: cleanNotes || undefined,
    laboratory: cleanLab || undefined,
    referenceProfile: payload.referenceProfile,
    warningThresholdPercent: payload.warningThresholdPercent,
  }
}

export const useExamStore = create<ExamStoreState>()(
  persist(
    (set, get) => ({
      exams: seedExams,
      examDictionary: seedExamDictionary,
      loaded: false,

      fetchExams: async () => {
        try {
          const { results } = await examsRepository.listResults()
          const exams = sortExamsByDateDesc(results.map(apiToExamRecord))
          set({ exams, examDictionary: buildExamDictionary(exams), loaded: true })
        } catch (error) {
          if (error instanceof AppError && error.code === 'UNAUTHORIZED') {
            logClientError('[login] fetchExams: sessão inválida (401)', error)
            useSessionStore.getState().logout()
            throw error
          }
          logClientError('[login] fetchExams: falha ao carregar', error)
          /* keep local state */
        }
      },

      addExam: (payload) => {
        const trimmedExamName = payload.examName.trim()
        if (!payload.patientId || !trimmedExamName) return null

        const normalizedExamName = normalizeIdentity(trimmedExamName)
        const newExam = createExamRecord({ ...payload, examName: trimmedExamName })

        set((state) => {
          const updatedExams = sortExamsByDateDesc([
            newExam,
            ...state.exams.map((exam) =>
              exam.normalizedExamName === normalizedExamName
                ? { ...exam, examName: trimmedExamName }
                : exam,
            ),
          ])
          return { exams: updatedExams, examDictionary: buildExamDictionary(updatedExams) }
        })

        const refBase = payload.referenceProfile?.base
        const repoPayload = {
          patientId: payload.patientId,
          examName: trimmedExamName,
          date: normalizeDate(payload.date),
          value: payload.value,
          laboratory: payload.laboratory,
          notes: payload.notes,
          refMin: refBase?.min,
          refMax: refBase?.max,
          warningThresholdPercent: payload.warningThresholdPercent,
        }
        retryOnNetworkOrAuthError(() => examsRepository.createResult(repoPayload))
          .then(({ result }) => {
            const persisted = apiToExamRecord(result)
            set((state) => {
              const withoutOptimistic = state.exams.filter((exam) => exam.id !== newExam.id)
              const deduped = withoutOptimistic.filter((exam) => exam.id !== persisted.id)
              const updatedExams = sortExamsByDateDesc([persisted, ...deduped])
              return { exams: updatedExams, examDictionary: buildExamDictionary(updatedExams) }
            })
          })
          .catch((error) => {
            if (error instanceof AppError && error.code === 'UNAUTHORIZED') {
              useSessionStore.getState().logout()
              return
            }
            if (!isLikelyOfflineError(error)) return
            useOutboxStore.getState().enqueue(
              'exam.create',
              repoPayload,
              getErrorMessage(error, 'Falha de conexão ao sincronizar exame.'),
            )
          })

        return newExam
      },

      importExams: (payloads) => {
        const validPayloads = payloads.filter(
          (payload) => payload.patientId && payload.examName.trim(),
        )
        if (!validPayloads.length) {
          return { created: 0, skipped: payloads.length }
        }

        const importedExams = validPayloads.map((payload) => createExamRecord(payload))

        set((state) => {
          const normalizedNameMap = new Map<string, string>()
          importedExams.forEach((exam) => {
            normalizedNameMap.set(exam.normalizedExamName, exam.examName)
          })

          const relabeledExisting = state.exams.map((exam) => {
            const latestName = normalizedNameMap.get(exam.normalizedExamName)
            return latestName ? { ...exam, examName: latestName } : exam
          })

          const updatedExams = sortExamsByDateDesc([...importedExams, ...relabeledExisting])
          return { exams: updatedExams, examDictionary: buildExamDictionary(updatedExams) }
        })

        return { created: importedExams.length, skipped: payloads.length - importedExams.length }
      },

      getExamsByPatient: (patientId) =>
        sortExamsByDateDesc(get().exams.filter((exam) => exam.patientId === patientId)),

      getRecentExams: (limit = 5) => sortExamsByDateDesc(get().exams).slice(0, limit),

      getExamSuggestions: (query, limit = 6) =>
        get()
          .examDictionary.filter((entry) => includesNormalized(entry.examName, query))
          .slice(0, limit)
          .map((entry) => entry.examName),
    }),
    {
      name: 'synap-exam-store',
      storage: indexedDBJSON,
      partialize: (state) => ({ exams: state.exams, examDictionary: state.examDictionary }),
      skipHydration: true,
    },
  ),
)
